import { workspacePrefix } from "./lib/workspaceAssertion";
/**
 * App — the whole route table for the funnel workspace.
 *
 * SCOPE. Every route below belongs to one tool: the funnel builder, its
 * orders, its analytics and its live board. There is intentionally no
 * dashboard, no settings tree and no unrelated modules — this package is the
 * Mineblock CRM, not a general admin shell. Add your own routes as siblings of
 * the `/app` block; keep the funnel block's paths as-is, because the pages
 * navigate to these literals internally.
 *
 * PATH PREFIX. The workspace base is `/app/production/listicle-builder`.
 * That string appears in `ListicleBuildersLayout` and in navigate() calls
 * across the pages, so changing it here alone will break in-app links —
 * grep for the literal before you move it.
 *
 * CODE-SPLITTING. Every route element is lazy so the initial bundle stays
 * small; the providers, shell, ProtectedRoute, LoginPage and Toaster are
 * statically imported because they are needed on first paint.
 */
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { AccessProvider } from "./contexts/AccessContext";
import { DateRangeProvider } from "./context/DateRangeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import EmbedBridge from "./components/EmbedBridge";
import AppShell from "./components/AppShell";
import LoginPage from "./pages/LoginPage";
import { Toaster } from "./components/ui/sonner";
import "./App.css";

const ListicleBuildersLayout = lazy(() => import("./pages/listiclebuilders/ListicleBuildersLayout"));
const LBDashboardPage = lazy(() => import("./pages/listiclebuilders/LBDashboardPage"));
const LBWebsitesPage = lazy(() => import("./pages/listiclebuilders/LBWebsitesPage"));
const LBNewWebsiteWizard = lazy(() => import("./pages/listiclebuilders/LBNewWebsiteWizard"));
const LBWebsiteWorkspace = lazy(() => import("./pages/listiclebuilders/LBWebsiteWorkspace"));
const LBTemplatesPage = lazy(() => import("./pages/listiclebuilders/LBTemplatesPage"));
const LBOrdersPage = lazy(() => import("./pages/listiclebuilders/LBOrdersPage"));
const LBSubscriptionsPanel = lazy(() => import("./pages/listiclebuilders/LBSubscriptionsPanel"));
const LBAbandonedCheckoutsPage = lazy(() => import("./pages/listiclebuilders/LBAbandonedCheckoutsPage"));
const LBCustomerProfilePage = lazy(() => import("./pages/listiclebuilders/LBCustomerProfilePage"));
const LBOrderDetailPage = lazy(() => import("./pages/listiclebuilders/LBOrderDetailPage"));
const LBFailedPaymentsPage = lazy(() => import("./pages/listiclebuilders/LBFailedPaymentsPage"));
const LBAnalyticsPage = lazy(() => import("./pages/listiclebuilders/LBAnalyticsPage"));
const LBSettingsPage = lazy(() => import("./pages/listiclebuilders/LBSettingsPage"));
const LBStorePaymentsPage = lazy(() => import("./pages/listiclebuilders/LBStorePaymentsPage"));
const LBCostsPage = lazy(() => import("./pages/listiclebuilders/LBCostsPage"));
const LiveViewPage = lazy(() => import("./pages/listiclebuilders/LiveViewPage"));

/** Route-level fallback. Never blanks the shell — only the outlet area. */
function RouteFallback() {
  return (
    <div className="flex h-full min-h-[240px] items-center justify-center text-sm text-muted-foreground" data-testid="route-loading">
      Loading…
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AccessProvider>
        <DateRangeProvider>
          <BrowserRouter basename={workspacePrefix(window.location.pathname) || "/"}>
            <Toaster />
            <EmbedBridge />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/login" element={<LoginPage />} />

                <Route element={<ProtectedRoute />}>
                  <Route path="/app" element={<AppShell />}>
                    <Route index element={<Navigate to="production/listicle-builder" replace />} />

                    <Route path="production/listicle-builder" element={<ListicleBuildersLayout />}>
                      <Route index element={<LBDashboardPage />} />
                      <Route path="websites" element={<LBWebsitesPage />} />
                      <Route path="websites/new" element={<LBNewWebsiteWizard />} />
                      <Route path="websites/:wid/*" element={<LBWebsiteWorkspace />} />
                      <Route path="templates" element={<LBTemplatesPage />} />
                      <Route path="orders" element={<LBOrdersPage />} />
                      <Route path="orders/subscriptions" element={<LBSubscriptionsPanel />} />
                      <Route path="orders/abandoned" element={<LBAbandonedCheckoutsPage />} />
                      <Route path="orders/customers" element={<LBCustomerProfilePage />} />
                      <Route path="orders/customers/:email" element={<LBCustomerProfilePage />} />
                      <Route path="orders/failed-payments" element={<LBFailedPaymentsPage />} />
                      {/* React Router v6 ranks by specificity, not source order,
                          so the static siblings above (subscriptions, abandoned,
                          customers, failed-payments) always win over :sessionId.
                          Listed last anyway so the precedence reads correctly. */}
                      <Route path="orders/:sessionId" element={<LBOrderDetailPage />} />
                      <Route path="analytics" element={<LBAnalyticsPage />} />
                      <Route path="settings" element={<LBSettingsPage />} />
                      {/* Store → Payments (S2-UI). Both paths mount the same
                          screen: without a store id it falls back to the
                          workspace's first store, so a link that predates a
                          store id still lands somewhere useful. */}
                      <Route path="stores/payments" element={<LBStorePaymentsPage />} />
                      <Route path="stores/:storeId/payments" element={<LBStorePaymentsPage />} />
                      <Route path="costs" element={<LBCostsPage />} />
                    </Route>

                    {/* Live View is a SIBLING of the layout, not a child: it is a
                        full-bleed board and must render without the workspace
                        rail. Keep it outside the block above. */}
                    <Route path="production/listicle-builder/live" element={<LiveViewPage />} />
                  </Route>
                </Route>

                <Route path="/" element={<Navigate to="/app/production/listicle-builder" replace />} />
                <Route path="*" element={<Navigate to="/app/production/listicle-builder" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </DateRangeProvider>
      </AccessProvider>
    </AuthProvider>
  );
}
